/** Admin workspaces and tools. Existing URLs remain the navigation contract. */
import {
  mdiAccountGroupOutline,
  mdiAccountMultipleOutline,
  mdiApi,
  mdiBatteryChargingOutline,
  mdiBellAlertOutline,
  mdiBellCheckOutline,
  mdiBellOutline,
  mdiBookOpenOutline,
  mdiChartLine,
  mdiChartTimelineVariant,
  mdiCounter,
  mdiDatabaseOutline,
  mdiDomain,
  mdiEmailNewsletter,
  mdiEmailOutline,
  mdiFileDocumentEditOutline,
  mdiHeartPulse,
  mdiKeyOutline,
  mdiLinkVariant,
  mdiMessageAlertOutline,
  mdiNewspaperVariantOutline,
  mdiPackageVariantClosed,
  mdiScriptTextPlayOutline,
  mdiStorefrontOutline,
  mdiTagOutline,
  mdiTextBoxOutline,
  mdiToolboxOutline,
  mdiTransitConnectionVariant,
  mdiTransmissionTower,
  mdiTrendingUp,
  mdiViewDashboardOutline,
} from '@mdi/js';

export const ADMIN_CAULDRON_IDS = { CRM: 'crm', COMMERCE: 'commerce', CMS: 'cms', AUDIT: 'audit', HUB: 'hub' };

export const ADMIN_CAULDRONS = [
  {
    id: 'crm',
    labelKey: 'admin.workspaces.crm',
    descriptionKey: 'admin.workspace_navigation.crm_description',
    accentClass: 'bg-theme-brand/10 text-theme-brand',
    icon: mdiAccountGroupOutline,
    defaultRoute: { name: 'admin-users' },
    tools: [
      {
        id: 'clients',
        labelKey: 'admin.workspace_tools.clients',
        routePrefixes: ['/users', '/tenants'],
        items: [
          {
            to: '/users',
            labelKey: 'admin.workspace_items.users',
            icon: mdiAccountGroupOutline,
          },
          {
            to: '/tenants',
            labelKey: 'admin.workspace_items.tenants',
            icon: mdiDomain,
          },
        ],
      },
      {
        id: 'requests',
        labelKey: 'admin.workspace_tools.requests',
        routePrefixes: ['/requests'],
        items: [
          {
            to: '/requests',
            labelKey: 'admin.workspace_items.requests',
            icon: mdiEmailOutline,
          },
        ],
      },
      {
        id: 'access',
        labelKey: 'admin.workspace_tools.access',
        routePrefixes: ['/invitations', '/billing/access-catalogue', '/api-keys'],
        items: [
          {
            to: '/invitations',
            labelKey: 'admin.workspace_items.invites',
            icon: mdiAccountGroupOutline,
          },
          {
            to: '/billing/access-catalogue',
            labelKey: 'admin.workspace_items.entitlements',
            icon: mdiBookOpenOutline,
          },
          {
            to: '/api-keys',
            labelKey: 'admin.workspace_items.api_keys',
            icon: mdiKeyOutline,
          },
        ],
      },
      {
        id: 'communications',
        labelKey: 'admin.workspace_tools.communications',
        routePrefixes: ['/notifications'],
        items: [
          {
            to: '/notifications',
            labelKey: 'admin.workspace_items.notifications',
            icon: mdiBellOutline,
            activeMatch: 'exact',
          },
        ],
      },
      {
        id: 'mailings',
        labelKey: 'admin.workspace_tools.mailings',
        routePrefixes: ['/newsroom/editions', '/newsroom/recipients'],
        items: [
          {
            to: '/newsroom/editions',
            labelKey: 'admin.workspace_items.editions',
            icon: mdiEmailNewsletter,
          },
          {
            to: '/newsroom/recipients',
            labelKey: 'admin.workspace_items.recipients',
            icon: mdiAccountMultipleOutline,
          },
        ],
      },
    ],
  },
  {
    id: 'commerce',
    labelKey: 'admin.workspaces.commerce',
    descriptionKey: 'admin.workspace_navigation.commerce_description',
    accentClass: 'bg-theme-palette-teal/10 text-theme-palette-teal',
    icon: mdiStorefrontOutline,
    defaultRoute: { name: 'admin-plans' },
    tools: [
      {
        id: 'products',
        labelKey: 'admin.workspace_tools.products',
        routePrefixes: ['/billing/plans', '/billing/api-datasets', '/billing/bundles', '/plans', '/stripe'],
        items: [
          {
            to: '/billing/plans',
            labelKey: 'admin.workspace_items.subscriptions',
            icon: mdiTextBoxOutline,
          },
          {
            to: '/billing/api-datasets',
            labelKey: 'admin.workspace_items.datasets',
            icon: mdiDatabaseOutline,
          },
          {
            to: '/billing/bundles',
            labelKey: 'admin.workspace_items.bundles',
            icon: mdiPackageVariantClosed,
          },
        ],
      },
      {
        id: 'promotions',
        labelKey: 'admin.workspace_tools.promotions',
        routePrefixes: ['/billing/packages', '/billing/promotions', '/billing/promotion-offers', '/billing/discounts'],
        // Packages are what an invited customer gets; discount codes are typed in a self-service checkout.
        items: [
          {
            to: '/billing/packages',
            labelKey: 'admin.workspace_items.packages',
            icon: mdiPackageVariantClosed,
          },
          {
            to: '/billing/discounts',
            labelKey: 'admin.workspace_items.discounts',
            icon: mdiTagOutline,
          },
          {
            to: '/billing/promotions',
            labelKey: 'admin.workspace_items.campaigns',
            icon: mdiTagOutline,
          },
          {
            to: '/billing/promotion-offers',
            labelKey: 'admin.workspace_items.promotions',
            icon: mdiTagOutline,
          },
        ],
      },
      {
        id: 'billing',
        labelKey: 'admin.workspace_tools.billing',
        routePrefixes: ['/billing/reference', '/token-rates', '/plan-quotas'],
        items: [
          { to: '/billing/reference/health', labelKey: 'admin.functions.billing_health', icon: mdiHeartPulse },
          { labelKey: 'admin.workspace_items.invoices', disabled: true },
        ],
      },
    ],
  },
  {
    id: 'cms',
    labelKey: 'admin.workspaces.cms',
    descriptionKey: 'admin.workspace_navigation.cms_description',
    accentClass: 'bg-theme-palette-indigo/10 text-theme-palette-indigo',
    icon: mdiFileDocumentEditOutline,
    defaultRoute: { name: 'admin-datasets' },
    tools: [
      {
        id: 'catalogue',
        labelKey: 'admin.workspace_tools.catalogue',
        routePrefixes: ['/datasets', '/visualizations', '/streams', '/config-editor'],
        items: [
          {
            to: '/datasets',
            labelKey: 'admin.workspace_items.datasets',
            icon: mdiDatabaseOutline,
          },
          {
            to: '/visualizations',
            labelKey: 'admin.workspace_items.visualizations',
            icon: mdiChartLine,
          },
          {
            to: '/streams',
            labelKey: 'admin.workspace_items.streams',
            icon: mdiTransitConnectionVariant,
          },
        ],
      },
      {
        id: 'content',
        labelKey: 'admin.workspace_tools.content',
        routePrefixes: ['/text', '/notification-templates'],
        items: [
          {
            to: '/text',
            labelKey: 'admin.workspace_items.translations',
            icon: mdiFileDocumentEditOutline,
          },
          { labelKey: 'admin.workspace_items.email_templates', disabled: true },
          { to: '/notification-templates', labelKey: 'admin.workspace_items.notification_templates', icon: mdiBellOutline },
        ],
      },
    ],
  },
  {
    id: 'audit',
    labelKey: 'admin.workspaces.audit',
    descriptionKey: 'admin.workspace_navigation.audit_description',
    accentClass: 'bg-theme-palette-amber/10 text-theme-palette-amber',
    icon: mdiToolboxOutline,
    defaultRoute: { name: 'admin-activity' },
    tools: [
      {
        id: 'forward-curve-evaluation',
        labelKey: 'admin.forward_curve_evaluation.title',
        direct: true,
        routePrefixes: ['/forward-curve-evaluation'],
        items: [{ to: '/forward-curve-evaluation/day-ahead', labelKey: 'admin.forward_curve_evaluation.title', icon: mdiChartLine }],
      },
      {
        id: 'forecast-evaluation',
        labelKey: 'admin.forecast_evaluation.title',
        direct: true,
        routePrefixes: ['/forecast-evaluation'],
        items: [{ to: '/forecast-evaluation/day-ahead', labelKey: 'admin.forecast_evaluation.title', icon: mdiChartLine }],
      },
      {
        id: 'analysis',
        labelKey: 'admin.workspace_tools.analysis',
        routePrefixes: ['/activity', '/data-health', '/mcp', '/notifications/sent', '/feedback'],
        items: [
          {
            to: '/activity',
            labelKey: 'admin.workspace_items.activity',
            icon: mdiChartTimelineVariant,
          },
          {
            to: '/data-health',
            labelKey: 'admin.workspace_items.data_health',
            icon: mdiHeartPulse,
          },
          {
            to: '/mcp',
            labelKey: 'admin.workspace_items.api_mcp',
            icon: mdiApi,
          },
          {
            to: '/notifications/sent',
            labelKey: 'admin.workspace_items.sent_notifications',
            icon: mdiBellCheckOutline,
          },
          {
            to: '/feedback',
            labelKey: 'admin.workspace_items.feedback',
            icon: mdiMessageAlertOutline,
          },
        ],
      },
      {
        id: 'user_content',
        labelKey: 'admin.workspace_tools.user_content',
        routePrefixes: ['/custom-assets/dashboards', '/custom-assets/kpis', '/tools/battery-configs', '/tools/congestion-contracts', '/tools/alerts', '/tools/runs', '/alert-targets'],
        items: [
          {
            to: '/custom-assets/dashboards',
            labelKey: 'admin.workspace_items.user_dashboards',
            icon: mdiViewDashboardOutline,
          },
          {
            to: '/custom-assets/kpis',
            labelKey: 'admin.workspace_items.user_kpis',
            icon: mdiCounter,
          },
          {
            to: '/tools/battery-configs',
            labelKey: 'admin.workspace_items.battery_configs',
            icon: mdiBatteryChargingOutline,
          },
          {
            to: '/tools/congestion-contracts',
            labelKey: 'admin.workspace_items.congestion_contracts',
            icon: mdiTransmissionTower,
          },
          {
            to: '/tools/alerts',
            labelKey: 'admin.workspace_items.alerts',
            icon: mdiBellAlertOutline,
          },
          {
            to: '/tools/runs',
            labelKey: 'admin.workspace_items.tool_runs',
            icon: mdiScriptTextPlayOutline,
          },
        ],
      },
      {
        id: 'sharing',
        labelKey: 'admin.workspace_tools.sharing',
        routePrefixes: ['/newsroom/share-links'],
        items: [
          {
            to: '/newsroom/share-links',
            labelKey: 'admin.workspace_items.share_links',
            icon: mdiLinkVariant,
          },
        ],
      },
    ],
  },
  {
    id: 'hub',
    labelKey: 'admin.workspaces.hub',
    descriptionKey: 'admin.workspace_navigation.hub_description',
    accentClass: 'bg-theme-palette-violet/10 text-theme-palette-violet',
    icon: mdiNewspaperVariantOutline,
    defaultRoute: { name: 'admin-newsroom' },
    hiddenRoutePrefixes: ['/knowledge-graph', '/articles', '/events', '/graph-nodes', '/entity-proposals', '/graph-review', '/entities', '/facts', '/graph-stats', '/graph-changelog', '/graph-runs'],
    tools: [
      {
        id: 'desk',
        labelKey: 'admin.workspace_tools.desk',
        direct: true,
        routePrefixes: ['/newsroom'],
        items: [
          {
            to: '/newsroom',
            labelKey: 'admin.workspace_items.desk',
            icon: mdiNewspaperVariantOutline,
            activeMatch: 'exact',
          },
        ],
      },
      {
        id: 'editorial',
        labelKey: 'admin.workspace_tools.editorial',
        direct: true,
        routePrefixes: ['/newsroom/overview', '/newsroom/read', '/newsroom/articles', '/newsroom/review'],
        items: [
          {
            to: '/newsroom/overview',
            labelKey: 'admin.workspace_items.article_overview',
            icon: mdiNewspaperVariantOutline,
            activeMatch: 'exact',
          },
        ],
      },
      {
        id: 'seo',
        labelKey: 'admin.workspace_tools.seo',
        routePrefixes: ['/seo-opportunities'],
        items: [
          {
            to: '/seo-opportunities',
            labelKey: 'admin.workspace_items.seo_opportunities',
            icon: mdiTrendingUp,
          },
        ],
      },
      {
        id: 'settings',
        labelKey: 'admin.workspace_tools.settings',
        routePrefixes: ['/newsroom/planning', '/newsroom/market-signals'],
        items: [
          {
            to: '/newsroom/planning',
            labelKey: 'admin.workspace_items.planning',
            icon: mdiFileDocumentEditOutline,
          },
          {
            to: '/newsroom/market-signals',
            labelKey: 'admin.workspace_items.market_signals',
            icon: mdiChartLine,
          },
        ],
      },
    ],
  },
];

export function getAdminCauldronById(id) {
  return ADMIN_CAULDRONS.find((workspace) => workspace.id === id) || null;
}

function matchesPath(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Longest segment prefix wins: delivery history belongs to Monitoring, compose to CRM. */
export function getAdminToolByPath(path) {
  if (!path) return null;
  let best = null;
  let bestLength = -1;
  for (const workspace of ADMIN_CAULDRONS) {
    for (const tool of workspace.tools) {
      for (const prefix of tool.routePrefixes) {
        if (matchesPath(path, prefix) && prefix.length > bestLength) {
          best = { workspace, tool };
          bestLength = prefix.length;
        }
      }
    }
  }
  return best;
}

export function getAdminCauldronByPath(path) {
  return getAdminToolByPath(path)?.workspace || ADMIN_CAULDRONS.find((workspace) =>
    workspace.hiddenRoutePrefixes?.some((prefix) => matchesPath(path || '', prefix))
  ) || null;
}
